// Which view the URL asks for, and which call.
//
// Two audiences, two routes:
//
//   /join?call=<id>   the customer. Unauthenticated, always. Video and nothing
//                     else. This is the link you send them.
//   /                 staff. Signed in, or shown the sign-in screen first.
//
// The route decides, not the session. An unauthenticated visitor to the base
// URL gets asked to sign in rather than being quietly dropped into a guest
// screen they did not ask for, and /join never asks a customer to sign in.

export type ViewName = 'guest' | 'inspector';

/** The customer's route. CloudFront maps unknown paths to index.html, so this
 *  is served by the SPA rather than 404ing. */
export const GUEST_PATH = '/join';

export type RouteState = {
  /** Channel to join, from ?call=. Null when the URL names none. */
  callId: string | null;
  /** True when the path is the guest route. */
  isGuestRoute: boolean;
  /** Explicit ?view= override, for testing either view from either route. */
  requested: ViewName | null;
  /** ?debug=1 - show the on-screen log. Works on both routes. */
  debug: boolean;
};

function onGuestPath(pathname: string): boolean {
  // Tolerates a trailing slash and any base path the app is mounted under.
  const normalized = pathname.replace(/\/+$/, '').toLowerCase();
  return normalized === GUEST_PATH || normalized.endsWith(GUEST_PATH);
}

export function readRoute(location: Location | URL = window.location): RouteState {
  const params = new URLSearchParams(location.search);
  const call = (params.get('call') ?? '').trim();
  const view = (params.get('view') ?? '').trim().toLowerCase();

  return {
    callId: call || null,
    isGuestRoute: onGuestPath(location.pathname),
    requested: view === 'guest' || view === 'inspector' ? view : null,
    debug: params.get('debug') === '1',
  };
}

/**
 * Note what this does NOT consider: whether anyone is signed in. A visitor to
 * the base URL is heading for the inspector view whether or not they have a
 * session - the caller shows them sign-in if they need it.
 */
export function resolveView(route: RouteState): ViewName {
  if (route.requested) return route.requested;
  return route.isGuestRoute ? 'guest' : 'inspector';
}

/** The link a guest is sent. Absolute, so it can be pasted into a text message. */
/**
 * A new call id.
 *
 * A UUID rather than anything readable, because this id is the only thing
 * standing between a stranger and a live conversation - the guest link admits
 * whoever holds it, and /liveswitch-token will mint a token for any channel id
 * somebody can name.
 */
export function newCallId(): string {
  return crypto.randomUUID();
}

export function guestLink(callId: string): string {
  const url = new URL(window.location.origin + GUEST_PATH);
  url.searchParams.set('call', callId);
  return url.toString();
}

/**
 * Reflect the current call in the address bar without reloading.
 *
 * Worth doing because the call ID otherwise lives only in React state: a
 * refresh mid-call would generate a new one and silently strand whoever was
 * already in the old channel.
 */
export function syncCallIdToUrl(callId: string | null): void {
  const url = new URL(window.location.href);
  if (callId) url.searchParams.set('call', callId);
  else url.searchParams.delete('call');
  window.history.replaceState({}, '', url.toString());
}
